/**
 * 承認（HIST §3.1・API §3.1b）— 案件の変更の行の `status` を上書きし、判断を操作履歴に足す。
 *
 * 承認が要るのは用語・ステージ・アクター・ドメインの 4 型（`APPROVAL_REQUIRED_ELEMENT_TYPES`）で、その型の
 * 行だけが `pending` で生まれる。承認は**行の状態を上書きする**操作であり、今の実装のように
 * 承認の行を別に積んで「在ること」で状態を表すことはしない。上書きなので、承認済みの行を
 * 書き直せば `planPjChanges` が `pending` を入れ直し、何もしなくても未承認へ戻る。
 *
 * **誰がいつ判断したかは操作履歴に足す。** 案件の変更の行は上書きされて最後に書いた人しか
 * 残らないので、判断者を辿れるのは操作履歴だけである。却下の理由は `reject` の行の `after`
 * に入れる（HIST §3.2）。
 *
 * **管理者のロールの照合はここでは行わない。** 実行できる人の限定はコンソールが PI Lab のポータルロール（`MANAGE_SPEC_GRAPH`）で行い、
 * 確定と同じ関門を通る（API §3.1b）。ここが守るのは、確定済みの案件に承認を積まないことと、
 * 承認の対象でない型の行を承認しないことの 2 つである。今の `approveTermChanges` と同じ立場で
 * ある。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行の状態を承認 / 却下で上書きし、誰がいつ判断したかを操作履歴に足す
 * @graph-connects planPjChanges [references] 書き直しで pending へ戻る側の規則
 */
import { APPROVAL_REQUIRED_ELEMENT_TYPES, decodeElementValue, isPjMerged, } from './pj-changes.js';
/**
 * 承認の判断 2 値。
 *
 * 今の `TERM_APPROVAL_DECISIONS` と同じ 2 値だが、値がそのまま案件の変更の行の `status` に
 * 入る点が違う（今は承認行の属性 JSON に入る）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 承認の判断 2 値の allowlist。値がそのまま案件の変更の行の状態になる
 * @graph-connects none
 */
export const ELEMENT_APPROVAL_DECISIONS = ['approved', 'rejected'];
/**
 * その行が承認の対象かを、行の `after` か `before` に畳まれた型から読む。
 *
 * 行は 7 列しかなく型の列を持たないので、値の中から取り出す（`decodeElementValue`）。消す行は
 * `after` が null なので `before` を読む。両方 null の行は行契約が作らせない。
 * @graph-connects none
 */
function approvalTargetTypeOf(row) {
    const subject = `案件 ${String(row.pjNumber)} のノード ${String(row.nodeId)}`;
    const json = row.after ?? row.before;
    if (json === null) {
        throw new Error(`spec-graph: ${subject} の行が前後どちらの値も持たない`);
    }
    return decodeElementValue(subject, json).nodeType;
}
/**
 * 判断を案件の変更の行と操作履歴へ落とす。**渡す行はその案件の行に限る。**
 *
 * 承認の対象になる 4 型以外の行を指されたら拒否する。業務や処理の行まで承認できると、
 * 「承認が要る型だけを人が見る」という関門そのものが意味を失う。
 *
 * `opIds` は `allocateIds('operations', nodeIds.length)` で取った範囲を先頭から当てる。
 *
 * @param args 案件番号・判断するノードの id・判断・却下の理由・その案件の今の行・採番した opId の範囲・判断の時刻・判断した主体
 * @returns 置き換えと操作履歴、または拒否
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 承認の判断を案件の変更の行の状態の上書きと操作履歴の行へ落とす
 * @graph-connects approvalTargetTypeOf [calls] 行に畳まれたノードの型の読み出し
 */
export function planElementApproval(args) {
    const count = args.opIds.to - args.opIds.from + 1;
    if (count !== args.nodeIds.length) {
        throw new Error(`採番した opId の個数が判断の件数と合わない（判断: ${String(args.nodeIds.length)} / 採番: ${String(count)}）`);
    }
    const rejections = [];
    if (isPjMerged(args.current)) {
        rejections.push({
            kind: 'rejected',
            reason: 'pj-already-merged',
            targetId: String(args.pjNumber),
            message: `案件 ${String(args.pjNumber)} は確定済みなので承認を積めない`,
            path: ['pjNumber'],
        });
    }
    const byNodeId = new Map(args.current.map((row) => [row.nodeId, row]));
    const targets = [];
    args.nodeIds.forEach((nodeId, index) => {
        const row = byNodeId.get(nodeId);
        if (row === undefined) {
            rejections.push({
                kind: 'rejected',
                reason: 'target-not-found',
                targetId: String(nodeId),
                message: `ノード ${String(nodeId)} の変更は案件 ${String(args.pjNumber)} にない`,
                path: ['nodeIds', String(index)],
            });
            return;
        }
        const nodeType = approvalTargetTypeOf(row);
        if (!APPROVAL_REQUIRED_ELEMENT_TYPES.some((type) => type === nodeType)) {
            rejections.push({
                kind: 'rejected',
                reason: 'input-schema',
                targetId: String(nodeId),
                message: `ノード ${String(nodeId)} は ${nodeType} の変更で、承認の対象（${APPROVAL_REQUIRED_ELEMENT_TYPES.join(' / ')}）ではない`,
                path: ['nodeIds', String(index)],
            });
            return;
        }
        targets.push(row);
    });
    if (rejections.length > 0)
        return { outcome: 'rejected', rejections };
    return {
        outcome: 'planned',
        clearNodeIds: targets.map((row) => row.nodeId),
        // 判断は行を増やさない。同じ鍵の行を、状態と「最後に書いた日時 / 主体」だけ替えて入れ直す。
        // 時刻を進めるのは、確定の衝突検出が案件の変更の行の updatedAt の最大値を見るためである
        // （決定 7）。承認が時刻を動かさないと、確定の最中に入った判断を検出できない。
        rows: targets.map((row) => ({
            ...row,
            status: args.decision,
            updatedAt: args.at,
            actor: args.actor,
        })),
        operations: targets.map((row, index) => ({
            opId: args.opIds.from + index,
            at: args.at,
            actor: args.actor,
            pjNumber: args.pjNumber,
            kind: args.decision === 'approved' ? 'approve' : 'reject',
            targetId: row.nodeId,
            // 判断は値を変えないので前の値を持たない。却下の理由だけを after に入れる（HIST §3.2）。
            before: null,
            after: args.decision === 'rejected' ? args.reason : null,
            refOpId: null,
        })),
    };
}
/**
 * 判断を反映する。置き換えと追記を、置き換え → 追記の順で投げる。
 *
 * 順が逆だと、追記だけが通って置き換えが落ちた場合に「判断の記録は在るのに状態が動いていない」
 * 行が残る。状態が先に動いていれば、記録の欠落は操作履歴の穴として後から埋められる。
 *
 * @param args 案件番号・判断するノードの id・判断・却下の理由・判断した主体
 * @param deps 置き場・採番・時刻
 * @returns 判断を反映したノードの一覧、または拒否
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 承認の判断を検査して案件の変更の行へ反映し、操作履歴へ足す
 * @graph-connects planElementApproval [calls] 判断の行への落とし込み
 */
export async function approveElementChanges(args, deps) {
    if (args.nodeIds.length === 0) {
        // 採番は 0 件の範囲を表せない（`IdRange` は両端を含む）。0 件の判断は入力の誤りなので、
        // 採番へ進む前にここで返す。
        return {
            outcome: 'rejected',
            rejections: [
                {
                    kind: 'rejected',
                    reason: 'input-schema',
                    targetId: String(args.pjNumber),
                    message: '判断するノードを 1 件以上指すこと',
                    path: ['nodeIds'],
                },
            ],
        };
    }
    const current = await deps.store.listPjChanges(args.pjNumber);
    const opIds = await deps.allocateIds('operations', args.nodeIds.length);
    const planned = planElementApproval({
        ...args,
        current,
        opIds,
        at: deps.now().toISOString(),
    });
    if (planned.outcome === 'rejected')
        return planned;
    await deps.store.replacePjChanges(args.pjNumber, {
        clearNodeIds: planned.clearNodeIds,
        rows: planned.rows,
    });
    await deps.store.appendOperations(planned.operations);
    return { outcome: 'applied', decidedNodeIds: planned.clearNodeIds };
}
//# sourceMappingURL=approval.js.map